import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "./api";
import { CAPABILITY_LABELS } from "@/components/brand/CapabilityBadges";

// Every number and row links to the Items page with the matching filters, so the count you click is
// the list you get (filters are URL parameters: bookmarkable).
export const itemsLink = (params) => `/admin/items?${new URLSearchParams(params).toString()}`;
const SERVERS = { type: "mcp_server", analysed: "true" };   // the denominator used on this page
const linkStyle = { color: "inherit", textDecoration: "none" };

function Stat({ label, value, sub, testid, to }) {
  return (
    <Link to={to} style={linkStyle} data-testid={testid}>
      <div style={{ minWidth: 190 }}>
        <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>{label}</div>
        <div style={{ fontSize: 26, color: "var(--hs-accent)" }}>{value == null ? "—" : `${value}%`}</div>
        <div style={{ fontSize: 12, color: "var(--hs-text-muted)", textDecoration: "underline dotted" }}>{sub}</div>
      </div>
    </Link>
  );
}

function Table({ rows, styles, title, testid, param }) {
  return (
    <div style={{ ...styles.card, flex: 1, minWidth: 320 }} data-testid={testid}>
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <table style={styles.tbl}>
        <thead><tr><th style={styles.th}>Value</th><th style={styles.th}>Servers</th><th style={styles.th}>% of analysed</th><th style={styles.th}>% of those needing secrets</th></tr></thead>
        <tbody>{rows.map((r) => {
          const to = itemsLink({ ...SERVERS, [param]: r.value });
          return (
            <tr key={r.value}>
              <td style={{ ...styles.td, fontFamily: "var(--hs-font-mono)" }}><Link to={to} style={{ color: "var(--hs-accent)" }} data-testid={`drill-${param}-${r.value}`}>{r.value}</Link></td>
              <td style={styles.td}><Link to={to} style={linkStyle}>{r.servers}</Link></td>
              <td style={styles.td}>{r.pct_of_analysed ?? "—"}%</td><td style={styles.td}>{r.pct_of_requiring ?? "—"}%</td></tr>);
        })}
        {rows.length === 0 && <tr><td style={styles.td} colSpan={4}>No data yet.</td></tr>}</tbody>
      </table>
    </div>
  );
}

const SC_LABELS = [  // [key, label, Items filter]
  ["versions_scanned", "Package versions scanned", { type: "package" }],
  ["packages_watched", "Packages watched", { type: "package", watched: "true" }],
  ["new_install_scripts", "Versions that added an install script", { rule: "new_install_script" }],
  ["risky_install_scripts", "Install scripts that download/run code", { rule: "install_script_risky" }],
  ["new_outbound_domains", "Versions with new outbound domains", { rule: "new_outbound_domain" }],
  ["new_credential_reads", "Versions with new credential reads", { rule: "new_credential_read" }],
  ["tool_description_changes", "Tool-description changes (packages)", { rule: "tool_description_change" }],
  ["typosquat_candidates", "Typosquat candidates", { type: "package", lookalike: "true" }],
  ["typosquat_threats", "Typosquats rated threat", { rule: "typosquat_lookalike", severity: "threat" }],
  ["remote_servers_probed", "Remote servers probed", { type: "mcp_remote" }],
  ["remote_tool_lists_changed", "Remote tool lists rewritten", { rule: "mcp_tools_rug_pull" }],
  ["servers_in_multiple_directories", "Servers listed in 2+ directories", { multi_directory: "true" }],
];

function SupplyChain({ sc, styles }) {
  return (
    <div style={styles.card} data-testid="stat-supply-chain">
      <h3 style={{ marginTop: 0 }}>Package &amp; remote-server research</h3>
      <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
        {SC_LABELS.map(([k, label, filter]) => (
          <Link key={k} to={itemsLink(filter)} style={linkStyle} data-testid={`sc-${k}`}>
            <div style={{ minWidth: 170 }}>
              <div style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>{label}</div>
              <div style={{ fontSize: 22, color: "var(--hs-accent)" }}>{sc[k] ?? 0}</div>
            </div>
          </Link>
        ))}
      </div>
      <table style={{ ...styles.tbl, marginTop: 12 }} data-testid="sc-per-source">
        <thead><tr><th style={styles.th}>Source</th><th style={styles.th}>Kind</th><th style={styles.th}>Items</th><th style={styles.th}>Snapshots</th></tr></thead>
        <tbody>{(sc.per_source || []).map((p) => (
          <tr key={p.source}>
            <td style={styles.td}>{p.source_id ? <Link to={itemsLink({ source_id: p.source_id })} style={{ color: "var(--hs-accent)" }} data-testid={`drill-source-${p.source}`}>{p.source}</Link> : p.source}</td>
            <td style={styles.td}>{p.kind}</td><td style={styles.td}>{p.items}</td><td style={styles.td}>{p.snapshots}</td></tr>))}
        {(sc.per_source || []).length === 0 && <tr><td style={styles.td} colSpan={4}>No sources yet.</td></tr>}</tbody>
      </table>
    </div>
  );
}

function QueueBox({ q, styles }) {
  if (!q) return null;
  return (
    <div style={{ ...styles.card, fontSize: 13 }} data-testid="stat-queue">
      <h3 style={{ marginTop: 0 }}>Review queue</h3>
      <Link to="/admin/review" style={{ color: "var(--hs-accent)" }}>{q.shown ?? 0} to review</Link>
      {" "}· {q.detection ?? 0} detections · {q.user_report ?? 0} user reports · {q.appeal_upheld ?? 0} appeals
      {" "}· <span data-testid="stat-queue-cleared">{q.cleared_by_model ?? 0} cleared by model (hidden)</span>
    </div>
  );
}

export default function StatsPage({ styles }) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.researchStats().then((r) => setS(r.data)).catch((ex) => setErr(ex.message)); }, []);
  if (err) return <div style={{ ...styles.card, color: "var(--hs-danger)" }}>Could not load stats: {err}</div>;
  if (!s) return <div>Loading…</div>;
  const A = s.servers_analysed;
  const steer = s.agent_steering || { servers: 0, pct: null, threat: 0 };
  const pay = s.payment_authorization || { servers: 0, pct: null, threat: 0, types: [] };
  const writes = s.writes_agent_instructions || { servers: 0, pct: null };
  const priv = s.high_privilege_scope || { servers: 0, pct: null, capabilities: [] };
  return (
    <div data-testid="stats-page">
      <h2>Research stats</h2>
      <div style={{ ...styles.card, fontSize: 12, color: "var(--hs-text-muted)" }} data-testid="stats-scope">
        <Link to={itemsLink(SERVERS)} style={{ color: "var(--hs-accent)" }} data-testid="drill-analysed">{A}</Link> of{" "}
        <Link to={itemsLink({ type: "mcp_server" })} style={{ color: "var(--hs-accent)" }} data-testid="drill-total">{s.servers_total}</Link>{" "}
        MCP servers analysed with rules <code>{s.detector_version}</code>
        {s.servers_not_yet_analysed > 0 && <> · <Link to={itemsLink({ type: "mcp_server", analysed: "false" })} style={{ color: "var(--hs-accent)" }}
          data-testid="drill-not-analysed">{s.servers_not_yet_analysed} not yet analysed</Link> — run <em>Dashboard → Score not-yet-analysed</em> to include them</>}.
        Percentages are of analysed servers. Click any number to open the matching items.
      </div>
      <div style={{ ...styles.card, display: "flex", gap: 32, flexWrap: "wrap" }}>
        <Stat testid="stat-secrets" label="Require secrets" value={s.requires_secrets.pct} sub={`${s.requires_secrets.servers} servers`}
              to={itemsLink({ ...SERVERS, requires_secrets: "true" })} />
        <Stat testid="stat-conversation" label="Ask for a secret in the conversation (warning)" value={s.secret_in_conversation.pct}
              sub={`${s.secret_in_conversation.servers} servers`} to={itemsLink({ ...SERVERS, secret_in_conversation: "true" })} />
        <Stat testid="stat-http" label="Remote over plain http (warning)" value={s.remote_plain_http.pct} sub={`${s.remote_plain_http.servers} servers`}
              to={itemsLink({ ...SERVERS, remote_http: "true" })} />
        <Stat testid="stat-steering" label="Steer AI agents (warning)" value={steer.pct}
              sub={`${steer.servers} servers${steer.threat ? ` · ${steer.threat} with data collection (threat)` : ""}`}
              to={itemsLink({ ...SERVERS, rule: "agent_steering" })} />
        <Stat testid="stat-payment" label="Handle payment authorizations (warning)" value={pay.pct}
              sub={`${pay.servers} servers${pay.threat ? ` · ${pay.threat} unlimited signing / key sending (threat)` : ""}`}
              to={itemsLink({ ...SERVERS, rule: "payment_authorization" })} />
        <Stat testid="stat-writes-instructions" label="Write agent instruction files (warning)" value={writes.pct}
              sub={`${writes.servers} servers`} to={itemsLink({ ...SERVERS, rule: "writes_agent_instructions" })} />
      </div>
      <div style={styles.card} data-testid="stat-capabilities">
        <h3 style={{ marginTop: 0 }}>Broad access (warning)</h3>
        <div style={{ fontSize: 14, marginBottom: 8 }}>
          <Link to={itemsLink({ ...SERVERS, rule: "high_privilege_scope" })} style={{ color: "var(--hs-accent)" }} data-testid="drill-high-privilege">
            {priv.servers} servers{priv.pct != null && ` (${priv.pct}%)`}</Link> have broad access to at least one of these.
        </div>
        {(priv.capabilities || []).map((c) => (
          <Link key={c.value} to={itemsLink({ ...SERVERS, capability: c.value })} data-testid={`drill-capability-${c.value}`}
                style={{ ...linkStyle, marginRight: 18 }}>
            {CAPABILITY_LABELS[c.value] || c.value}: <strong>{c.servers}</strong>{c.pct_of_analysed != null && ` (${c.pct_of_analysed}%)`}</Link>))}
      </div>
      {(pay.types || []).length > 0 && (
        <div style={styles.card} data-testid="stat-payment-types">
          <h3 style={{ marginTop: 0 }}>Payment authorization types</h3>
          {pay.types.map((t) => (
            <Link key={t.value} to={itemsLink({ ...SERVERS, payment_type: t.value })} data-testid={`drill-payment_type-${t.value}`}
                  style={{ ...linkStyle, marginRight: 18 }}>
              <code>{t.value}</code>: <strong>{t.servers}</strong>{t.pct_of_analysed != null && ` (${t.pct_of_analysed}%)`}</Link>))}
        </div>)}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <Table styles={styles} rows={s.secret_types} title="Most common secret types" testid="stat-types" param="secret_type" />
        <Table styles={styles} rows={s.secret_names} title="Most common secret names" testid="stat-names" param="secret_name" />
      </div>
      <QueueBox q={s.review_queue} styles={styles} />
      {s.supply_chain && <SupplyChain sc={s.supply_chain} styles={styles} />}
      <div style={styles.card} data-testid="stat-severity">
        <h3 style={{ marginTop: 0 }}>Analysed servers by worst severity</h3>
        {["threat", "warning", "info", "none"].map((k) => (
          <Link key={k} to={itemsLink({ ...SERVERS, severity: k })} style={{ ...linkStyle, marginRight: 18 }} data-testid={`drill-severity-${k}`}>
            {k}: <strong>{s.severity[k] || 0}</strong></Link>))}
      </div>
    </div>
  );
}
